import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { yaml } from '@codemirror/lang-yaml';
import YAML from 'yaml';
import type { NoteTypeSchemaDef } from '@cortex/shared';
import { Api } from '../../lib/api';
import { useTypes, qk } from '../../hooks/useVault';
import { useUi } from '../../store/ui';
import { cortexTheme, cortexHighlight } from '../editor/theme';
import { TypeDot } from '../ui/TypeDot';

/** Note type editor: the YAML file is the truth; this edits it in place. Plus the schema-drift list. */
export function TypesView() {
  const types = useTypes();
  const qc = useQueryClient();
  const ui = useUi();
  const selectedKey = useUi((s) => s.typesKey) ?? types.data?.[0]?.key ?? null;
  const def = types.data?.find((t) => t.key === selectedKey) ?? null;
  const drift = useQuery({ queryKey: qk.drift, queryFn: Api.types.drift, staleTime: 15_000 });
  const [text, setText] = useState('');
  const [dirty, setDirty] = useState(false);
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);

  const initial = useMemo(() => (def ? YAML.stringify({ ...def }, { lineWidth: 0 }) : ''), [def]);

  useEffect(() => {
    setText(initial);
    setDirty(false);
    if (!host.current) return;
    view.current?.destroy();
    view.current = new EditorView({
      parent: host.current,
      state: EditorState.create({
        doc: initial,
        extensions: [
          cortexTheme,
          cortexHighlight,
          lineNumbers(),
          yaml(),
          history(),
          keymap.of([...defaultKeymap, ...historyKeymap]),
          EditorView.theme({ '.cm-gutters': { display: 'flex', background: 'var(--bg-base)', color: 'var(--text-faint)', border: 'none' }, '.cm-content': { fontFamily: 'var(--font-mono)', fontSize: '12.5px', maxWidth: 'none', padding: '12px' } }),
          EditorView.updateListener.of((u) => {
            if (u.docChanged) {
              setText(u.state.doc.toString());
              setDirty(true);
            }
          }),
        ],
      }),
    });
    return () => view.current?.destroy();
  }, [initial]);

  const save = async () => {
    if (!def) return;
    try {
      const parsed = YAML.parse(text) as Partial<NoteTypeSchemaDef>;
      await Api.types.save(def.key, parsed);
      qc.invalidateQueries({ queryKey: qk.types });
      qc.invalidateQueries({ queryKey: qk.drift });
      qc.invalidateQueries({ queryKey: ['note'] });
      setDirty(false);
      ui.toast(`Saved .cortex/types/${def.key}.yaml`);
    } catch (err) {
      ui.toast(`Save failed: ${(err as Error).message}`, 'crit');
    }
  };

  const createType = async () => {
    const key = window.prompt('New type key (lowercase, e.g. "vehicle"):');
    if (!key || !/^[a-z][a-z0-9_-]*$/.test(key)) return;
    try {
      await Api.types.save(key, { key, label: key[0].toUpperCase() + key.slice(1), fieldSchema: { type: 'object', properties: {} }, template: `# {{title}}\n`, folder: '00-Inbox' });
      qc.invalidateQueries({ queryKey: qk.types });
      ui.setView('types', key);
    } catch (err) {
      ui.toast((err as Error).message, 'crit');
    }
  };

  const driftForType = (drift.data ?? []).filter((d) => !def || d.type === def.key);

  return (
    <div className="types-view">
      <div className="types-list">
        <div className="label" style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 6px' }}>
          Note types <button className="ghost" style={{ fontSize: 10, padding: '0 6px' }} onClick={() => void createType()}>+ new</button>
        </div>
        {(types.data ?? []).map((t) => (
          <button key={t.key} className={t.key === selectedKey ? 'active' : ''} onClick={() => ui.setView('types', t.key)}>
            <TypeDot type={t.key} /> {t.label}
            <span className="mono" style={{ marginLeft: 'auto', color: 'var(--text-faint)', fontSize: 10 }}>{(drift.data ?? []).filter((d) => d.type === t.key).length || ''}</span>
          </button>
        ))}
      </div>
      <div className="types-editor">
        <div className="head">
          <span className="mono">.cortex/types/{def?.key ?? '…'}.yaml</span>
          {def?.builtin && <span className="chip">built-in</span>}
          <span style={{ flex: 1 }} />
          <span className="save-state" style={{ color: dirty ? 'var(--orange)' : undefined }}>{dirty ? '● unsaved' : ''}</span>
          <button className="primary" disabled={!dirty} onClick={() => void save()}>Save (ADMIN)</button>
          {def && !def.builtin && (
            <button className="danger" onClick={() => window.confirm(`Delete type ${def.key}?`) && Api.types.remove(def.key).then(() => (qc.invalidateQueries({ queryKey: qk.types }), ui.setView('types', null)))}>
              Delete
            </button>
          )}
        </div>
        <div ref={host} style={{ minHeight: 0, overflow: 'hidden' }} />
        <div className="drift">
          <div className="label">
            Schema drift {def ? `· ${def.label}` : ''} <span className="count">{driftForType.length}</span>
            <span style={{ color: 'var(--text-faint)', marginLeft: 8 }}>advisory — these notes still open, index and search</span>
          </div>
          {driftForType.slice(0, 200).map((d) => (
            <div key={d.id} className="drift-row" onClick={() => ui.openNote(d.id)}>
              <span>{d.title} <span className="mono" style={{ color: 'var(--text-faint)' }}>{d.path}</span></span>
              <span className="w">{d.schemaWarnings.map((w) => `${w.path} ${w.message}`).join('; ')}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
